import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Timer } from '../components/Timer';
import { CheckInModal } from '../components/CheckInModal';
import { TodaySummary, HistoryChart, TrendPoint } from '../components/Reports';
import { SettingsModal } from '../components/SettingsModal';
import { Button } from '../components/Button';
import { getTodayKey, getSettings as getLocalSettings, saveSettings } from '../services/storage';
import { AppSettings, Session, DailyStats, User } from '../types';
import { useNavigate } from 'react-router-dom';
import { listIntervals, createInterval, IntervalRecord } from '../services/intervals';
import { getSession } from '../services/session';
import type { ApiError } from '../services/api';
import { deleteReports } from '../services/reports';
import { playSound, startAlarm, stopAlarm } from '../services/sound';
import { useTheme } from '../components/ThemeProvider';
import { deleteAccount } from '../services/account';
import { getSettingsRemote, updateSettingsRemote } from '../services/settings';
import { ConfirmModal } from '../components/ConfirmModal';

export const AppPage: React.FC = () => {
  const navigate = useNavigate();
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [lastDuration, setLastDuration] = useState(20);
  const [todayStats, setTodayStats] = useState<Session[]>([]);
  const [historyStats, setHistoryStats] = useState<DailyStats[]>([]);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [dataError, setDataError] = useState<string | null>(null);
  const [settings, setSettings] = useState<AppSettings>(getLocalSettings());
  const [settingsDraft, setSettingsDraft] = useState<AppSettings>(getLocalSettings());
  const [settingsDirty, setSettingsDirty] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [restartSignal, setRestartSignal] = useState(0);
  const [trendPeriod, setTrendPeriod] = useState<'today' | '7d' | 'month' | 'year'>('7d');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { theme, setTheme } = useTheme();
  const [authStatus, setAuthStatus] = useState<'checking' | 'authenticated' | 'unauthenticated'>('checking');
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [confirmState, setConfirmState] = useState<{
    title: string;
    message: string;
    confirmLabel: string;
    destructive?: boolean;
    onConfirm: () => void;
  } | null>(null);
  
  const toSession = (record: IntervalRecord): Session => ({
    id: record.id,
    dateKey: record.dateKey,
    startTime: record.startTs,
    endTime: record.endTs,
    durationMinutes: record.intervalMinutes,
    productive: record.answer === 'yes',
    pauseCount: record.pauseCount,
    totalPausedSeconds: record.pausedSeconds,
  });

  const dateKeyDaysAgo = (days: number) => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString().split('T')[0];
  };

  const getRange = (period: typeof trendPeriod) => {
    if (period === 'today') {
      const today = getTodayKey();
      return { start: today, end: today };
    }
    if (period === '7d') {
      return { start: dateKeyDaysAgo(6), end: getTodayKey() };
    }
    if (period === 'month') {
      return { start: dateKeyDaysAgo(29), end: getTodayKey() };
    }
    return { start: dateKeyDaysAgo(364), end: getTodayKey() };
  };

  const formatMonthLabel = (dateKey: string) => {
    const [year, month] = dateKey.split('-').map(Number);
    const date = new Date(year, month - 1, 1);
    return date.toLocaleString(undefined, { month: 'short' });
  };

  // Guard clause for auth
  useEffect(() => {
    const init = async () => {
      try {
        const session = await getSession();
        if (!session.user) {
          setAuthStatus('unauthenticated');
          navigate('/login');
          return;
        }
        const user: User = {
          id: session.user.id,
          email: session.user.email,
          role: session.user.role,
          newsletter: session.user.newsletterOptIn,
          createdAt: Date.now(),
        };
        setCurrentUser(user);
        setAuthStatus('authenticated');
      } catch (err) {
        setAuthStatus('unauthenticated');
        setDataError(err instanceof Error ? err.message : 'Unable to load your session.');
      }
    };

    init();
  }, [navigate]);

  useEffect(() => {
    if (authStatus !== 'authenticated') {
      return;
    }
    let isMounted = true;
    getSettingsRemote()
      .then((remote) => {
        if (!isMounted) return;
        setSettings(remote);
        setSettingsDraft(remote);
        setTheme(remote.theme);
        setSettingsLoaded(true);
      })
      .catch((err) => {
        if (!isMounted) return;
        setDataError(err instanceof Error ? err.message : 'Unable to load settings.');
        setSettingsLoaded(true);
      });

    return () => {
      isMounted = false;
    };
  }, [authStatus, setTheme]);

  // Load Data
  const loadData = useCallback(async () => {
    setDataError(null);
    let allSessions: Session[] = [];

    try {
      const range = getRange(trendPeriod);
      const response = await listIntervals(range.start, range.end);
      allSessions = response.items.map(toSession);
    } catch (err) {
      const status = (err as ApiError).status;
      if (status === 401) {
        navigate('/login');
        return;
      }
      setDataError(err instanceof Error ? err.message : 'Unable to load data.');
    }

    const todayKey = getTodayKey();
    const today = allSessions.filter((s) => s.dateKey === todayKey);
    setTodayStats(today);

    const statsMap = new Map<string, DailyStats>();
    allSessions.forEach((s) => {
      const prev = statsMap.get(s.dateKey) || {
        dateKey: s.dateKey,
        totalSessions: 0,
        productiveCount: 0,
        unproductiveCount: 0,
        productivityRate: 0,
        totalTimeMinutes: 0,
      };
      prev.totalSessions++;
      if (s.productive) prev.productiveCount++;
      else prev.unproductiveCount++;
      prev.totalTimeMinutes += s.durationMinutes;
      prev.productivityRate = Math.round((prev.productiveCount / prev.totalSessions) * 100);
      statsMap.set(s.dateKey, prev);
    });

    const statsArray = Array.from(statsMap.values()).sort((a, b) => a.dateKey.localeCompare(b.dateKey));
    setHistoryStats(statsArray);
  }, [navigate, trendPeriod]);

  useEffect(() => {
    if (authStatus !== 'authenticated') {
      return;
    }
    loadData();
  }, [authStatus, loadData]);

  const handleIntervalComplete = (duration: number) => {
    setLastDuration(duration);
    setCheckInOpen(true);
  };

  const handleCheckInAnswer = async (productive: boolean) => {
    setCheckInOpen(false);
    stopAlarm();
    try {
      await createInterval({
        dateKey: getTodayKey(),
        intervalMinutes: lastDuration,
        startTs: Date.now() - (lastDuration * 60 * 1000),
        endTs: Date.now(),
        answer: productive ? 'yes' : 'no',
        pauseCount: 0,
        pausedSeconds: 0,
      });
    } catch (err) {
      const status = (err as ApiError).status;
      if (status === 401) {
        navigate('/login');
        return;
      }
      setDataError(err instanceof Error ? err.message : 'Unable to save interval.');
      return;
    }
    setRestartSignal((value) => value + 1);
    await loadData();
  };

  useEffect(() => {
    if (!settingsLoaded) {
      return;
    }
    saveSettings(settings);
  }, [settings, settingsLoaded]);

  useEffect(() => {
    if (checkInOpen && settings.soundEnabled) {
      startAlarm(settings.soundId, settings.volume);
      return () => stopAlarm();
    }
    stopAlarm();
  }, [checkInOpen, settings.soundEnabled, settings.soundId, settings.volume]);

  const handleDeleteData = async () => {
    setConfirmState({
      title: 'Delete report data?',
      message: 'This will permanently remove all your report data.',
      confirmLabel: 'Delete data',
      destructive: true,
      onConfirm: async () => {
        try {
          await deleteReports();
          await loadData();
        } catch (err) {
          setDataError(err instanceof Error ? err.message : 'Unable to delete data.');
        } finally {
          setConfirmState(null);
        }
      },
    });
  };

  const handleDeleteAccount = async () => {
    setConfirmState({
      title: 'Delete your account?',
      message: 'This will permanently delete your account and all stored data.',
      confirmLabel: 'Delete account',
      destructive: true,
      onConfirm: async () => {
        try {
          await deleteAccount();
          navigate('/');
        } catch (err) {
          setDataError(err instanceof Error ? err.message : 'Unable to delete account.');
        } finally {
          setConfirmState(null);
        }
      },
    });
  };

  const getTrendPoints = (): { title: string; data: TrendPoint[] } => {
    const todayKey = getTodayKey();
    if (trendPeriod === 'today') {
      const total = todayStats.length;
      const yesCount = todayStats.filter((s) => s.productive).length;
      const rate = total > 0 ? Math.round((yesCount / total) * 100) : 0;
      return { title: 'Today Productivity', data: [{ name: 'Today', rate, total }] };
    }

    if (trendPeriod === 'year') {
      const monthly = new Map<string, { yes: number; total: number }>();
      historyStats.forEach((stat) => {
        const monthKey = stat.dateKey.slice(0, 7);
        const current = monthly.get(monthKey) ?? { yes: 0, total: 0 };
        current.yes += stat.productiveCount;
        current.total += stat.totalSessions;
        monthly.set(monthKey, current);
      });

      const data = Array.from(monthly.entries())
        .sort((a, b) => a[0].localeCompare(b[0]))
        .map(([key, value]) => ({
          name: formatMonthLabel(key),
          total: value.total,
          rate: value.total > 0 ? Math.round((value.yes / value.total) * 100) : 0,
        }));

      return { title: 'Last 12 Months', data };
    }

    const range = getRange(trendPeriod);
    const start = new Date(`${range.start}T00:00:00`);
    const end = new Date(`${range.end}T00:00:00`);
    const data: TrendPoint[] = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dateKey = d.toISOString().split('T')[0];
      const stat = historyStats.find((item) => item.dateKey === dateKey);
      data.push({
        name: dateKey.slice(5),
        rate: stat?.productivityRate ?? 0,
        total: stat?.totalSessions ?? 0,
      });
    }

    return {
      title: trendPeriod === '7d' ? 'Last 7 Days' : 'Last 30 Days',
      data,
    };
  };

  const user = currentUser;
  const trend = getTrendPoints();

  if (authStatus === 'checking' || (authStatus === 'authenticated' && !settingsLoaded)) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-muted">
        Loading your account...
      </div>
    );
  }

  if (authStatus === 'unauthenticated') {
    return null;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-bold text-text">Welcome back, {user?.email.split('@')[0]}</h1>
              <p className="text-muted">Ready to maintain your flow?</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSettingsDraft(settings);
                setSettingsDirty(false);
                setSettingsOpen(true);
              }}
            >
              Settings
            </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Left Column: Timer */}
            <div className="lg:col-span-5 space-y-8">
                <Timer 
                    initialMinutes={settings.defaultInterval} 
                    onIntervalComplete={handleIntervalComplete} 
                    settings={settings}
                    restartSignal={restartSignal}
                    alarmActive={checkInOpen}
                    soundEnabled={settings.soundEnabled}
                />
                
                <div className="bg-primary/10 border border-primary/20 p-4 rounded-lg">
                    <h3 className="text-sm font-bold text-text mb-1">Tip for accuracy</h3>
                    <p className="text-xs text-muted">Keep this tab open for the most accurate timer. Sounds will play even if you are working in another window.</p>
                </div>
            </div>

            {/* Right Column: Stats */}
            <div className="lg:col-span-7 space-y-8">
                <section>
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-bold text-text">Today's Overview</h2>
                        <span className="text-xs bg-slate-100 text-muted px-2 py-1 rounded">{getTodayKey()}</span>
                    </div>
                    {dataError && <p className="text-sm text-no mb-3">{dataError}</p>}
                    <TodaySummary sessions={todayStats} />
                </section>
                
                <section>
                    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                      <h2 className="text-lg font-bold text-text">Productivity Trend</h2>
                      <div className="flex gap-2">
                        {[
                          { id: 'today', label: 'Today' },
                          { id: '7d', label: '7 Days' },
                          { id: 'month', label: 'Month' },
                          { id: 'year', label: 'Year' },
                        ].map((option) => (
                          <button
                            key={option.id}
                            onClick={() => setTrendPeriod(option.id as typeof trendPeriod)}
                            className={`px-3 py-1 text-xs font-semibold rounded-full border transition-colors ${
                              trendPeriod === option.id
                                ? 'bg-primary text-white border-primary'
                                : 'border-border text-muted hover:text-text'
                            }`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <HistoryChart title={trend.title} data={trend.data} />
                </section>
            </div>
        </div>

        <CheckInModal isOpen={checkInOpen} onAnswer={handleCheckInAnswer} soundEnabled={settings.soundEnabled} />
        <SettingsModal
          isOpen={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          settings={settingsDraft}
          onSettingsChange={(next) => {
            setSettingsDraft(next);
            setSettingsDirty(true);
          }}
          onSave={async () => {
            setSettingsSaving(true);
            try {
              const saved = await updateSettingsRemote(settingsDraft);
              setSettings(saved);
              setTheme(saved.theme);
              setSettingsOpen(false);
              setSettingsDirty(false);
            } catch (err) {
              setDataError(err instanceof Error ? err.message : 'Unable to save settings.');
            } finally {
              setSettingsSaving(false);
            }
          }}
          canSave={settingsDirty}
          isSaving={settingsSaving}
          theme={settingsDraft.theme}
          onThemeChange={(nextTheme) => {
            setSettingsDraft((prev) => ({ ...prev, theme: nextTheme }));
            setSettingsDirty(true);
          }}
          onDeleteData={handleDeleteData}
          onDeleteAccount={handleDeleteAccount}
          onTestSound={() => {
            if (settingsDraft.soundEnabled) {
              playSound(settingsDraft.soundId, settingsDraft.volume);
            }
          }}
        />
        <ConfirmModal
          isOpen={Boolean(confirmState)}
          title={confirmState?.title ?? ''}
          message={confirmState?.message ?? ''}
          confirmLabel={confirmState?.confirmLabel ?? 'Confirm'}
          destructive={confirmState?.destructive}
          onConfirm={() => confirmState?.onConfirm()}
          onCancel={() => setConfirmState(null)}
        />
    </div>
  );
};
